// BulkActionsBar - UPDATED WITH API INTEGRATION
// Phase 4 - Day 1: Bulk Actions with Real API Calls
import React, { useState } from 'react';
import styled from 'styled-components';
import { FiX, FiCheck, FiUserPlus, FiArchive } from 'react-icons/fi';
import { theme } from '@theme';
import { bulkActionsApi } from '@/services/api/notifications.api';

interface BulkActionsBarProps {
  selectedIds: string[];
  onClearSelection: () => void;
  onActionComplete: () => void;
}

// Styled Components (same as before)
const Container = styled.div<{ visible: boolean }>`
  position: fixed;
  bottom: ${props => props.visible ? '24px' : '-100px'};
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.xl};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  transition: all ${theme.transitions.medium};
  z-index: 1000;
  min-width: 700px;
  border: 2px solid ${theme.colors.border};
`;

const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.text};
  font-size: ${theme.typography.sizes.md};
  font-weight: ${theme.typography.weights.semibold};
`;

const Count = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 ${theme.spacing.sm};
  background: ${theme.colors.primary};
  color: white;
  border-radius: ${theme.borderRadius.full};
  font-weight: ${theme.typography.weights.bold};
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-left: auto;
`;

const ActionButton = styled.button<{ variant?: 'success' | 'danger' | 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  ${props => {
    switch (props.variant) {
      case 'success':
        return `
          background: ${theme.colors.success};
          color: white;
          &:hover { background: ${theme.colors.success}dd; }
        `;
      case 'danger':
        return `
          background: ${theme.colors.error};
          color: white;
          &:hover { background: ${theme.colors.error}dd; }
        `;
      case 'primary':
        return `
          background: ${theme.colors.primary};
          color: white;
          &:hover { background: ${theme.colors.primaryDark}; }
        `;
      default:
        return `
          background: white;
          color: ${theme.colors.text};
          border: 2px solid ${theme.colors.border};
          &:hover { background: ${theme.colors.background}; }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 18px;
  }
`;

const ClearButton = styled(ActionButton)`
  background: transparent;
  color: ${theme.colors.textSecondary};
  border: none;

  &:hover {
    color: ${theme.colors.error};
    background: ${theme.colors.error}10;
  }
`;

// Modal Components
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all ${theme.transitions.medium};
`;

const ModalContent = styled.div`
  background: white;
  padding: ${theme.spacing.xxl};
  border-radius: ${theme.borderRadius.lg};
  max-width: 500px;
  width: 90%;
  box-shadow: ${theme.shadows.xl};
`;

const ModalHeader = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const ModalTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const ModalMessage = styled.p`
  font-size: ${theme.typography.sizes.md};
  color: ${theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.xl};
`;

const ProgressContainer = styled.div`
  margin-top: ${theme.spacing.lg};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.secondary});
  width: ${props => props.progress}%;
  transition: width ${theme.transitions.medium};
`;

const ProgressText = styled.div`
  margin-top: ${theme.spacing.sm};
  text-align: center;
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.textSecondary};
`;

export const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  selectedIds,
  onClearSelection,
  onActionComplete,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [currentAction, setCurrentAction] = useState<'validate' | 'reject' | 'assign' | 'archive' | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleActionClick = (action: 'validate' | 'reject' | 'assign' | 'archive') => {
    setCurrentAction(action);
    setShowModal(true);
  };

  const handleConfirm = async () => {
    if (!currentAction) return;

    try {
      setIsProcessing(true);
      setProgress(0);

      // Convert string IDs to numbers
      const numericIds = selectedIds.map(id => parseInt(id, 10));

      // Create bulk action via API
      const bulkAction = await bulkActionsApi.create({
        action_type: currentAction,
        target_ids: numericIds,
        metadata: currentAction === 'reject' ? { reason: 'Rejeté en masse' } : undefined,
      });

      // Poll for progress
      await bulkActionsApi.pollProgress(
        bulkAction.id,
        (progressData) => {
          setProgress(progressData.percentage);
        },
        1000 // Poll every second
      );

      // Success
      setShowModal(false);
      setIsProcessing(false);
      setProgress(0);
      setCurrentAction(null);

      // Notify parent components
      onActionComplete();
      onClearSelection();

      // Show success message (you can replace this with a toast notification)
      alert(`Action "${getActionLabel(currentAction)}" terminée avec succès!`);

    } catch (error) {
      console.error('Bulk action failed:', error);
      setIsProcessing(false);
      setProgress(0);
      alert('Erreur lors de l\'action en masse. Veuillez réessayer.');
    }
  };

  const handleCancel = () => {
    if (!isProcessing) {
      setShowModal(false);
      setCurrentAction(null);
      setProgress(0);
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'validate': return 'Valider';
      case 'reject': return 'Rejeter';
      case 'assign': return 'Assigner';
      case 'archive': return 'Archiver';
      default: return action;
    }
  };

  const getModalMessage = () => {
    if (!currentAction) return '';

    const count = selectedIds.length;
    const label = getActionLabel(currentAction);

    return `Êtes-vous sûr de vouloir ${label.toLowerCase()} ${count} dossier${count > 1 ? 's' : ''} ?`;
  };

  return (
    <>
      <Container visible={selectedIds.length > 0}>
        <SelectionInfo>
          <FiCheck />
          <Count>{selectedIds.length}</Count>
          <span>sélectionné{selectedIds.length > 1 ? 's' : ''}</span>
        </SelectionInfo>

        <Actions>
          <ActionButton
            variant="success"
            onClick={() => handleActionClick('validate')}
            disabled={isProcessing}
          >
            <FiCheck />
            Valider
          </ActionButton>

          <ActionButton
            variant="danger"
            onClick={() => handleActionClick('reject')}
            disabled={isProcessing}
          >
            <FiX />
            Rejeter
          </ActionButton>

          <ActionButton
            variant="primary"
            onClick={() => handleActionClick('assign')}
            disabled={isProcessing}
          >
            <FiUserPlus />
            Assigner
          </ActionButton>

          <ActionButton
            onClick={() => handleActionClick('archive')}
            disabled={isProcessing}
          >
            <FiArchive />
            Archiver
          </ActionButton>

          <ClearButton onClick={onClearSelection} disabled={isProcessing}>
            <FiX />
            Annuler
          </ClearButton>
        </Actions>
      </Container>

      {/* Confirmation Modal */}
      <ModalOverlay isOpen={showModal} onClick={handleCancel}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              Confirmer l'action
            </ModalTitle>
            <ModalMessage>
              {getModalMessage()}
            </ModalMessage>
          </ModalHeader>

          {isProcessing && (
            <ProgressContainer>
              <ProgressBar>
                <ProgressFill progress={progress} />
              </ProgressBar>
              <ProgressText>
                Traitement en cours... {Math.round(progress)}%
              </ProgressText>
            </ProgressContainer>
          )}

          <ModalActions>
            <ActionButton onClick={handleCancel} disabled={isProcessing}>
              Annuler
            </ActionButton>
            <ActionButton
              variant="primary"
              onClick={handleConfirm}
              disabled={isProcessing}
            >
              {isProcessing ? 'Traitement...' : 'Confirmer'}
            </ActionButton>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};
